import { isInterrupt } from './card';
import type { RoomConfig } from './config';
import type { CardTitle, CardUid, PlayerId } from './ids';
import type { CardRegistry } from './pack';
import type { RngState } from './rng';

/**
 * 游戏状态。
 *
 * ⚠️ 三条硬约束（`architecture.md` §3.1 / §4）：
 * 1. **全部可 JSON 序列化** —— 只用数组和普通对象，**不用 `Map` / `Set`**
 * 2. **只用整数** —— 没有浮点（浮点只在渲染层用）
 * 3. **这是「完整状态」，只有房主有**。客户端拿到的是 `project()` 之后的视图
 */

// ============================================================
// 卡牌实例
// ============================================================

/**
 * 一张具体卡牌的实例。
 *
 * 同一个牌名可以有多张（比如 4 张 `vanilla:Exception`），
 * 所以手牌和牌堆里存的是 `CardUid`，再回来这里查它是哪张牌。
 */
export interface CardInstance {
  readonly title: CardTitle;
}

// ============================================================
// 玩家
// ============================================================

export interface PlayerState {
  readonly id: PlayerId;
  readonly name: string;
  /** 手牌。内部是 uid，顺序有意义（影响 UI 排布） */
  readonly hand: readonly CardUid[];
  /** 出局后**整手牌进弃牌堆**，这里就空了（`docs/rules.md` §9.2） */
  readonly alive: boolean;
  readonly connected: boolean;
}

// ============================================================
// 回合
// ============================================================

/**
 * 当前该谁行动、还要行动几次。
 *
 * 攻击叠加（`docs/rules.md` §5.5）的官方例子是 **2 → 4 → 6**，所以：
 *
 * ```
 * 被攻击者打出攻击时：下一家的 turnsRemaining = 本家 turnsRemaining + 2
 * ```
 *
 * 但普通回合的 `turnsRemaining` 也是 1，直接套公式会变成 3（错，应该是 2）。
 * 所以额外用 `fromAttack` 区分「这是我自己的普通回合」还是「我是被攻击欠的债」：
 *
 * ```
 * 下一家 turnsRemaining = (本家 fromAttack ? 本家 turnsRemaining : 0) + 2
 * ```
 */
export interface TurnSlot {
  readonly player: PlayerId;
  /** 还要行动几次（**包含正在进行这一次**）。普通回合 = 1 */
  readonly turnsRemaining: number;
  /** 这些回合是不是「被攻击欠下的」 */
  readonly fromAttack: boolean;
  /**
   * **额外回合的来源牌名**（`fromAttack` 时非空）。
   *
   * UI 拿它去查那张牌的定义、读它的**辨识色** —— 所以按钮的颜色跟着
   * 攻击卡走，将来出别的颜色的攻击牌也不用改界面（用户要求）。
   * 普通回合 / 老快照没有这个字段 = null。
   */
  readonly attackSource?: CardTitle | null;
}

/**
 * 回合内的阶段。
 *
 * ⚠️ **故意不是一个字段。** 它完全由 `resolution` / `decision` / `turn` 派生：
 *
 * | 条件 | 阶段 |
 * | --- | --- |
 * | `resolution !== null` | 等休想窗口，谁都不能动 |
 * | `decision !== null` | 等某个玩家做抉择 |
 * | 两个都是 null | 自由出牌阶段 |
 *
 * 派生状态不留第二份 —— 同一个事实有两处，迟早对不上。
 * 「结算中」更是从来不可观察：`reduce` 一次调用是原子的，
 * 中间态不会出现在任何 `GameState` 里。
 *
 * 判定统一走 `canAct()`。
 */

// ============================================================
// 出牌与休想链
// ============================================================

/** 一次出牌的种类 */
export type PlayKind =
  /** 单张 */
  | 'single'
  /** 两张同名 → 随机抢一张 */
  | 'pair'
  /** 三张同名 → 指定牌名索要 */
  | 'triple'
  /** 五张不同名 → 从弃牌堆任取一张（游戏规则） */
  | 'fiveDifferent';

export interface PendingPlay {
  readonly player: PlayerId;
  /** 这次打出的所有牌 */
  readonly cards: readonly CardUid[];
  readonly kind: PlayKind;
  /** 连击指定的目标玩家 */
  readonly target: PlayerId | null;
  /** 三张同名时指定的牌名 */
  readonly namedTitle: CardTitle | null;
  /** 五张不同名时，从弃牌堆挑中的那张 */
  readonly discardPick: CardUid | null;
}

/**
 * **这一手打出去之后要不要等玩家做选择。**
 *
 * 判据是**动作本身**，不是"最后有没有真的弹出抉择"：
 *
 * | 出牌 | 要选什么 |
 * | --- | --- |
 * | 单张（需目标，如求值） | 选目标 |
 * | 一对 | 选对手，再选抽他哪一张 |
 * | 三张同名 | 选目标 + 选牌名 |
 * | 五张不同名 | 从弃牌堆挑一张 |
 *
 * 即使"目标只剩一张、没得挑"而跳过了选择阶段，它**仍然算需要选择** ——
 * 房主侧的 0.5s 宽限对这类行动一律不留（见 `host/rules.ts`）。
 */
export function playNeedsChoice(play: PendingPlay): boolean {
  return play.target !== null || play.kind !== 'single';
}

/** 休想链里的一环 */
export interface NopeEntry {
  readonly player: PlayerId;
  readonly card: CardUid;
}

/**
 * 一次出牌的**响应窗口**。
 *
 * `docs/rules.md` §9.5：实体桌上「休想」靠抢喊，线上必须显式建模。
 *
 * ## 两阶段模型（`architecture.md` §3.3）
 *
 * 一次出牌拆成两件**互相独立**的事：
 *
 * 1. **卡牌移动** —— 出牌当场执行，**永不撤销**。打出的行动牌和所有休想牌
 *    在窗口还开着的时候就已经躺在弃牌堆里了（实体桌上也是这样）。
 * 2. **效果结算** —— 窗口关闭**之后**才执行，被取消时**根本不执行**。
 *
 * 所以这里**不需要回滚**，也就不需要快照。效果是否生效只看链长奇偶：
 *
 * ```
 * 取消 ⟺ nopeChain.length % 2 === 1     // 0 生效 / 1 取消 / 2 Yup 生效 / …
 * ```
 *
 * ⚠️ `play.cards` 和 `nopeChain[].card` 里存的 uid **只是为了动画**，
 * 这些牌的所有权已经在 `discardPile` 里了。别把它们当成第二份位置，
 * 否则会写出重复入弃牌堆的 bug。
 *
 * ⚠️ 这个字段是**单槽，不是栈** —— 一次出牌只有一个窗口，
 * 休想链在同一个窗口里增长，不会嵌套。
 */
export interface Resolution {
  readonly play: PendingPlay;
  /**
   * 这一手**是否已经打出去（松手提交）**。
   *
   * - `false` = 「举着」：牌还在手里，打断窗口已经开着，对手此刻就能打断；
   * - `true` = 已提交：牌已进弃牌堆，等宽限期走完再应用效果。
   *
   * ⚠️ 由它决定"打断时要不要没收那张牌"以及房主的超时时长。
   */
  committed: boolean;
  /**
   * 打断链，按时间顺序。**奇偶决定效果是否被取消**；长度也是"打断量计数"——
   * 房主据此延长 deadline（每张 +1s，上限 3s）。
   *
   * ⚠️ 新模型里**没有"过牌 / 全员表态"**：窗口由出牌动画 + 房主定时关闭，
   * 玩家只能打 [断点]，或者什么都不做。
   */
  readonly nopeChain: readonly NopeEntry[];
}

// ⚠️ `Resolution` 里**故意没有 deadline 字段**。
//
// 窗口**只由显式的 `window/timeout` 动作关闭**，reducer 不读时钟、也不比较时间。
// 时长（含 RTO 倍率、「没人持牌时假装等多久」）全是房主侧的策略，
// 只决定房主**什么时候发出那个 timeout 动作** —— 而那个动作本身就在棋谱里。
//
// 这样 `GameState` 里**一个时间字段都没有**，一串 action 就能完整复现一局。
// 见 `architecture.md` §3.8。

// ============================================================
// 待抉择
// ============================================================

/**
 * 需要某个玩家给出输入时挂在这里。
 *
 * 每个分支都写明 `player` —— reducer 只接受该玩家的 `decide` 动作，
 * 别人的一律拒绝。
 */
export type Decision =
  /** 拆弹后，把致命牌插回抽牌堆的任意位置（**私密**：只下发给本人） */
  | { readonly kind: 'insertLethal'; readonly player: PlayerId; readonly card: CardUid }
  /** 抽到致命牌：用不用救命牌。允许主动送死（`docs/rules.md` §9.7） */
  | { readonly kind: 'useRescue'; readonly player: PlayerId; readonly card: CardUid }
  /**
   * 从自己手牌里挑一张交出去。
   *
   * - `namedTitle === null` → 恩惠：随便挑一张（给的人决定）
   * - `namedTitle !== null` → 三张同名：只能交这个牌名（手里没有就什么都不发生，
   *   那种情况根本不会进到这个抉择里）
   */
  | {
      readonly kind: 'giveCard';
      readonly player: PlayerId;
      readonly to: PlayerId;
      readonly namedTitle: CardTitle | null;
    }
  /** 五张不同名：从弃牌堆里挑一张（候选由 reducer 列好，避免客户端自由发挥） */
  | {
      readonly kind: 'pickFromDiscard';
      readonly player: PlayerId;
      readonly candidates: readonly CardUid[];
    }
  /**
   * **两张同名：抽牌的人从对方手里挑一张牌背。**
   *
   * ⚠️ 这一条改过语义：原来是 `applyPair` **当场随机**抽一张，
   * 现在改成"开一个抉择、由抽牌的人挑一个**位次**"。
   *
   * 理由是评审要的那个交互：「被抽牌的人能够在这个期间通过调换手牌顺序
   * 来避免抽牌人猜中他重要手牌在哪一张，然后抽牌人面前会有一副牌背
   * 在他面前同步调换顺序……双向完整模拟现实中进行抽牌的实况。」
   * 没有"挑"这个动作，整套交互就无从谈起 —— 随机抽的话，牌背扇
   * 只是一个装饰。
   *
   * ⚠️ 抽牌的人挑的是**位次**（`index`），不是牌 —— 他不知道位次背后是什么。
   * 真实顺序由被抽的人在结算时报上来（见 `DecisionInput` 的 `order`）。
   */
  | {
      readonly kind: 'stealCard';
      /** 由谁挑（= 打出一对的那个人） */
      readonly player: PlayerId;
      /** 从谁手里挑 */
      readonly from: PlayerId;
      /** 有几张牌背可以挑。**客户端据此铺那一扇牌背** */
      readonly count: number;
    };

// ============================================================
// 已知信息（玩家的「记忆」）
// ============================================================

export interface KnownCard {
  /** 从牌堆顶往下数第几张（0 = 顶上那张） */
  readonly indexFromTop: number;
  readonly title: CardTitle;
}

/**
 * 某个玩家**私有**的已知信息。
 *
 * ⚠️ 这不是状态本身，而是「这个人知道什么」。两者必须分开建模 ——
 * 否则「我打过 Debug，看过头三张」这种信息就没地方放（`architecture.md` §3.4）。
 */
/** 我确定"这些牌在抽牌堆里"，但不知道具体位次的**期望**张数（实数） */
export interface KnownInDraw {
  readonly title: CardTitle;
  readonly count: number;
}

/** 我确定"这些牌在某位玩家手里"的**期望**张数（实数） */
export interface KnownInHand {
  readonly player: PlayerId;
  readonly title: CardTitle;
  readonly count: number;
}

/**
 * 某个玩家**私有**的已知信息 —— 三层：
 *
 * | 层 | 字段 | 含义 |
 * | --- | --- | --- |
 * | 定位 | `knownTop` | 我知道第 i 张是什么 |
 * | 区域 | `knownInDraw` | 我知道这些牌在抽牌堆里，但不知道第几张（洗牌之后的产物） |
 * | 区域 | `knownInHand` | 我知道这些牌在某位玩家手里（看着他抽走我已知的牌） |
 *
 * ⚠️ 这不是状态本身，而是「这个人知道什么」。两者必须分开建模 ——
 * 否则「我打过 Debug，看过头三张」这种信息就没地方放（`architecture.md` §3.4）。
 *
 * ⚠️ `knownInDraw` / `knownInHand` 的 `count` 是**实数期望**（用户可以接受"被抽去
 * 零点几张"这种量）。详见 `docs/tracker-belief-report.md`。
 */
export interface Knowledge {
  /** 我确定的、从堆顶往下第 i 张是什么（Debug / 拆弹插入之后产生） */
  readonly knownTop: readonly KnownCard[];
  /** 在抽牌堆里、但不知道位次的期望张数 */
  readonly knownInDraw: readonly KnownInDraw[];
  /** 确定在某位玩家手里的期望张数 */
  readonly knownInHand: readonly KnownInHand[];
}

// ============================================================
// 随机数
// ============================================================

/** splitmix32 的状态（定义在 `rng.ts`，这里re-export 方便用） */
export type { RngState } from './rng';

// ============================================================
// 状态本体
// ============================================================

export type GamePhase =
  /** 大厅：还没开局，房主在配游戏规则 */
  | 'lobby'
  /** 对局中 */
  | 'playing'
  /** 结束 */
  | 'finished';

export interface GameState {
  readonly phase: GamePhase;

  /** 房主 = 权威节点 */
  readonly host: PlayerId;

  /** 当前游戏规则（公开信息）。开局后冻结 */
  readonly config: RoomConfig;

  /** 按**座位顺序**排列。数组下标就是座位号 */
  readonly players: readonly PlayerState[];

  /**
   * 全部卡牌实例。
   * **下标就是 `CardUid`** —— 省掉一层 `uid → 卡` 的查找表，也保证了确定性。
   */
  readonly cards: readonly CardInstance[];

  /** 抽牌堆。**数组末尾 = 堆顶**（`docs/rules.md` §9.1） */
  readonly drawPile: readonly CardUid[];

  /** 弃牌堆。**公开信息**，所有人都能看到 */
  readonly discardPile: readonly CardUid[];

  /** 该谁行动。`phase !== 'playing'` 时为 null */
  readonly turn: TurnSlot | null;

  /** 正在进行的那次出牌的响应窗口 */
  readonly resolution: Resolution | null;

  /** 正在等谁做什么抉择 */
  readonly decision: Decision | null;

  /** 玩家各自的私有已知信息，key = PlayerId */
  readonly knowledge: Readonly<Record<string, Knowledge>>;

  /** 随机数发生器状态 */
  readonly rng: RngState;

  readonly winner: PlayerId | null;
}

// ============================================================
// 查询辅助（纯函数，不修改 state）
// ============================================================

/**
 * 当前是否轮到 `actor` 自由行动（出牌 / 抽牌）。
 *
 * 这是**唯一**判定「能不能出牌」的地方 —— 因为回合阶段是派生的，不存字段。
 * 三件事都会挡住行动：还没开局、不是你的回合、窗口或抉择未完成。
 */
export function canAct(state: GameState, actor: PlayerId): boolean {
  return (
    state.phase === 'playing' &&
    state.turn?.player === actor &&
    findPlayer(state, actor)?.alive === true &&
    state.resolution === null &&
    state.decision === null
  );
}

/** 是否有一个休想窗口开着（开着的时候谁都不能出牌） */
export function windowIsOpen(state: GameState): boolean {
  return state.resolution !== null;
}

/** 是否在等某个玩家做抉择 */
export function isAwaitingDecision(state: GameState): boolean {
  return state.decision !== null;
}

/** 当前玩家还要行动几次（不在对局中时为 0） */
export function turnsLeft(state: GameState): number {
  return state.turn?.turnsRemaining ?? 0;
}

/**
 * 谁有资格在这次响应窗口里打休想。
 *
 * - 手上要有带 `interrupt` 特征的牌
 * - 存活，除非 `config.deadPlayersCanNope` 放宽了这条
 * - **出牌者本人也算**（官方只说 "any player"，不加特判就是允许）
 *
 * 房主用它决定「这次窗口是空窗还是有人的窗口」，从而选不同的等待时长 ——
 * 但那纯粹是时序策略，reducer 不用它做任何判定（见 `architecture.md` §3.3）。
 */
export function nopeEligible(state: GameState, registry: CardRegistry): readonly PlayerId[] {
  return state.players
    .filter((player) => player.alive || state.config.deadPlayersCanNope)
    .filter((player) =>
      player.hand.some((uid) => {
        const inst = state.cards[uid];
        if (inst === undefined) return false;
        const def = registry.get(inst.title);
        return def !== undefined && isInterrupt(def);
      }),
    )
    .map((player) => player.id);
}

export function findPlayer(state: GameState, id: PlayerId): PlayerState | undefined {
  return state.players.find((p) => p.id === id);
}

/** 座位号；不在座返回 -1 */
export function seatOf(state: GameState, id: PlayerId): number {
  return state.players.findIndex((p) => p.id === id);
}

export function cardAt(state: GameState, uid: CardUid): CardInstance | undefined {
  return state.cards[uid];
}

/**
 * 从 `from` 开始顺时针找下一个存活玩家。
 * 找不到（只剩自己或全死）返回 undefined。
 */
export function nextAliveSeat(state: GameState, from: PlayerId): number {
  const start = seatOf(state, from);
  if (start < 0) return -1;
  const n = state.players.length;
  for (let step = 1; step <= n; step++) {
    const seat = (start + step) % n;
    if (state.players[seat]?.alive === true) return seat;
  }
  return -1;
}

export function aliveCount(state: GameState): number {
  return state.players.reduce((n, p) => n + (p.alive ? 1 : 0), 0);
}

export function emptyKnowledge(): Knowledge {
  return { knownTop: [], knownInDraw: [], knownInHand: [] };
}

/** 合并一张"在抽牌堆里但不知道位次"的已知牌 */
export function addKnownInDraw(knowledge: Knowledge, title: CardTitle, count = 1): Knowledge {
  if (count <= 0) return knowledge;
  const index = knowledge.knownInDraw.findIndex((entry) => entry.title === title);
  const knownInDraw = [...knowledge.knownInDraw];
  if (index >= 0) {
    const at = knownInDraw[index]!;
    knownInDraw[index] = { title, count: at.count + count };
  } else {
    knownInDraw.push({ title, count });
  }
  return { ...knowledge, knownInDraw };
}

/** 合并一张"确定在某位玩家手里"的已知牌 */
export function addKnownInHand(
  knowledge: Knowledge,
  player: PlayerId,
  title: CardTitle,
  count = 1,
): Knowledge {
  if (count <= 0) return knowledge;
  const index = knowledge.knownInHand.findIndex((entry) => entry.player === player && entry.title === title);
  const knownInHand = [...knowledge.knownInHand];
  if (index >= 0) {
    const at = knownInHand[index]!;
    knownInHand[index] = { player, title, count: at.count + count };
  } else {
    knownInHand.push({ player, title, count });
  }
  return { ...knowledge, knownInHand };
}

/**
 * **洗牌：把定位降级成区域。**
 *
 * Hash 只置换抽牌堆，不会把牌洗进任何人的手牌 —— 所以"堆顶几张是什么"变成
 * "这几张还在抽牌堆里、只是不知道位次"。这是**非冗余**信息（隐藏区 = 抽牌堆 ∪
 * 对手手牌），不能像以前那样清空。
 */
export function downgradeLocatedToDraw(knowledge: Knowledge): Knowledge {
  if (knowledge.knownTop.length === 0) return knowledge;
  let next = knowledge;
  for (const card of knowledge.knownTop) next = addKnownInDraw(next, card.title, 1);
  return { ...next, knownTop: [] };
}

/**
 * **区域稀释。** 抽牌堆里共有 D 张、抽走顶部一张后：每张"已知在牌堆"的牌被抽走
 * 的概率是 `1/D`（对称性），所以剩下的期望乘 `factor = (D−1)/D`。
 *
 * 返回剩下的知识，以及**被抽走那部分**的期望（调用方把它加进"抽者手里"）。
 * 这一步的一阶矩是**精确**的（线性期望）。
 */
export function diluteKnownInDraw(
  knowledge: Knowledge,
  factor: number,
): { knowledge: Knowledge; drawn: readonly KnownInDraw[] } {
  const knownInDraw: KnownInDraw[] = [];
  const drawn: KnownInDraw[] = [];
  for (const entry of knowledge.knownInDraw) {
    const left = entry.count * factor;
    const gone = entry.count - left;
    if (left > 1e-9) knownInDraw.push({ title: entry.title, count: left });
    if (gone > 1e-9) drawn.push({ title: entry.title, count: gone });
  }
  return { knowledge: { ...knowledge, knownInDraw }, drawn };
}

/**
 * **牌堆顶被抽走一张之后，定位信息跟着挪。**
 *
 * `drawPile` 的末尾是堆顶，抽牌 = `pop()`。所以：
 * - 原来 `indexFromTop === 0` 的那张已经离开牌堆（进了某人的手牌），删掉；
 * - 其余每一张离堆顶都近了一位，`indexFromTop - 1`。
 *
 * ⚠️ 这是**纯函数**，对所有人的 `knowledge` 都用同一套 —— 别人抽牌也会让
 *    "我看到的牌序"往上挪，不挪的话记牌器会显示错位的情报。
 */
export function knownTopAfterDraw(knowledge: Knowledge): Knowledge {
  const knownTop: KnownCard[] = [];
  for (const card of knowledge.knownTop) {
    if (card.indexFromTop <= 0) continue;
    knownTop.push({ indexFromTop: card.indexFromTop - 1, title: card.title });
  }
  return { ...knowledge, knownTop };
}

/**
 * **往牌堆里插入一张牌之后，已知信息跟着挪。**
 *
 * `drawPile` 下标 0 是堆底、末尾是堆顶；`index` 是插入的数组下标。
 * 一张已知牌在插入之后：
 * - 它的数组位次 >= `index`（在插入点靠堆顶一侧）→ 被顶高一位，
 *   但它**离堆顶的距离不变**；
 * - 否则位次不变，但整叠长了一张 → **离堆顶远了一位**。
 *
 * `oldLength` 是插入**之前**的牌堆张数。
 */
export function knownTopAfterInsert(knowledge: Knowledge, oldLength: number, index: number): Knowledge {
  return {
    ...knowledge,
    knownTop: knowledge.knownTop.map((card) => {
      const arrayPos = oldLength - 1 - card.indexFromTop;
      return arrayPos < index
        ? { indexFromTop: card.indexFromTop + 1, title: card.title }
        : card;
    }),
  };
}

/**
 * **记下一张确定的牌在堆顶往下第几张。**
 *
 * 只有"亲手把某张牌插回牌堆"的人知道这件事 —— 所以调用方只对 `action.actor` 用。
 */
export function knownTopLearn(knowledge: Knowledge, indexFromTop: number, title: CardTitle): Knowledge {
  const knownTop = knowledge.knownTop
    .filter((card) => card.indexFromTop !== indexFromTop)
    .concat({ indexFromTop, title })
    .sort((a, b) => a.indexFromTop - b.indexFromTop);
  return { ...knowledge, knownTop };
}
